import {
  Box,
  Grid,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import BottomHeader from './Bottom';
import { useLocation, useNavigate } from 'react-router';
import ObjectPreview from 'src/components/ObjectPreview';
import { ONLINE_MEDIA_CLIPPING } from 'src/route';
import GroupObject from 'src/components/GroupObject';
import ModalManageObjectByGroup from 'src/components/ModalManageObjectByGroup';
import { useEffect, useMemo, useState } from 'react';
import useQueryUrl from 'src/hooks/useQuery';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TooltipCustomize from 'src/components/TooltipCustomize';
import TabNetworkAnalysis from 'src/components/TabNetworkAnalysis';
import ProductTourTooltip from 'src/components/ProductTourTooltip';
import { useScrolls } from '../Sidebar/ScrollContext';
import EcommerceObjectPreview from 'src/components/EcommerceObjectPreview';
import BottomHeaderEcommerce from './BottomEcommerce';
import useObjectManagement from 'src/services/useObjectManagement';
import { useAlert } from 'src/hooks/useAlert';
import useEcommerce from 'src/services/useEcommerce';
import {
  reducerUpdateIsSelectedAllPrimary,
  reducerUpdateStreamEcommerceSelected
} from 'src/features/ecommerceStream';
import { reducerUpdateObjectListByObjectType } from 'src/features/objectManagement';
import { reducerUpdateAnalyticArgumentObjectIdList } from 'src/features/analyticArguments';
import { concatUniqueObjectsById } from 'src/utils/concatUniqueObjectsById';
import ModalManageObjectRetail from 'src/components/Retail/ModalManageObjectRetail';

interface ProductTourProps {
  isOnTour: boolean;
}

const HeaderWrapper = styled(Box)<ProductTourProps>(
  ({ theme, isOnTour }) => `
    color: ${theme.header.textColor};
    padding: ${theme.spacing(1, 4, 0, 4)};
    right: 0;
    ${!isOnTour && 'z-index: 5;'}
    box-shadow: ${theme.header.boxShadow};
    justify-content: space-between;
    width: 100%;
    background: ${theme.header.background};
    @media (min-width: ${theme.breakpoints.values.lg}px) {
        left: ${theme.sidebar.width};
        width: auto;
    }
`
);

const HeaderWrapperSec = styled(Box)<ProductTourProps>(
  ({ theme, isOnTour }) => `
    background: #fbfbfb;
    color: ${theme.header.textColor};
    padding: ${theme.spacing(1, 4, 0, 4)};
    right: 0;
    ${!isOnTour && 'z-index: 5;'}
    justify-content: space-between;
    width: 100%;
    @media (min-width: ${theme.breakpoints.values.lg}px) {
        left: ${theme.sidebar.width};
        width: auto;
    }
`
);

function Header() {
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const menu = pathname.split('/')[1];
  const title = pathname.split('/')[2];
  const query = useQueryUrl();
  const tabQuery = query.get('tab');
  const navigate = useNavigate();
  const [tab, setTab] = useState<string>(tabQuery);
  const setTitle = () => {
    switch (title) {
      case 'social_media':
        return 'Social Media Stream';
      default:
        return title.charAt(0).toUpperCase() + title.slice(1);
    }
  };

  const { objectListByObjectType } = useAppSelector(
    (state) => state.objectManagement
  );

  const objectListPrevies = useMemo(() => {
    if (title === 'content' || title === 'community') {
      return objectListByObjectType.filter(
        (object) => object.streamType === 'account'
      );
    }
    return objectListByObjectType;
  }, [objectListByObjectType, title]);

  const handleChange = (_: React.SyntheticEvent, newValue: string) => {
    setTab(newValue);
    navigate(`${pathname}?tab=${newValue}`);
  };

  useEffect(() => {
    setTab(tabQuery);
  }, [tabQuery]);

  const textTooltip = () => {
    switch (title.toLowerCase()) {
      case 'overview':
        return 'Overview metrics of object performance';
      case 'content':
        return 'Discover engagement performance of social profiles';
      case 'community':
        return 'Discover followers analysis and its growth of social profiles';
      case 'campaign':
        return 'Discover Hashtags performance';
      case 'conversation':
        return 'Discover all mentions and sentiment analysis of social profiles, keywords and hashtags';
      case 'comparison':
        return 'Comparison analysis between each object, group of object or label';
      default:
        return '';
    }
  };

  const isNetworkAnalysis = () => {
    return title === 'sna' || title === 'dna';
  };

  const isClustering = () => {
    return title === 'clustering';
  };

  const isGoogleReviewMenu = menu === 'google_review';
  const isEcommerce = menu === 'ecommerce';

  const {
    isOnTour,
    currentTour: { stepNumber, menu: menuStep, submenu }
  } = useAppSelector((state) => state.storeProductTour);
  const projectId = useAppSelector((state) => state.users.project.id);
  const {
    groupSelectedById: selectedGroupId,
    // groupObject: prevGroupObject,
    selectedGroups
  } = useAppSelector((state) => state.objectManagement);
  const { objectIdList } = useAppSelector((state) => state.analyticArguments);

  const objectList = useAppSelector(
    (state) => state.objectManagement.objectList.data
  );

  const refs = useScrolls();

  const { getGroupObject } = useObjectManagement();
  const { handleClickAlert } = useAlert();
  const { useGetEcommerceObjectList } = useEcommerce();

  const [getObjectList] = useGetEcommerceObjectList();

  const handleRefresh = () => {
    dispatch(reducerUpdateStreamEcommerceSelected([]));
    dispatch(reducerUpdateIsSelectedAllPrimary(false));
    Promise.all([
      getObjectList({
        variables: {
          projectId
        }
      }),
      getGroupObject({ projectId: projectId, templateName: 'content' })
    ])
      .then((responses) => {
        // if a group is selected
        if (selectedGroupId !== '_default' || selectedGroups.length > 0) {
          const newGroupObjectList = responses[1].data;
          const prevSelectedGroups = selectedGroups;

          const prevSelectedGroupIds = prevSelectedGroups
            .map((group) => group.objectList.map((group) => group.id))
            .flat();

          const newSelectedGroups = newGroupObjectList.filter((group) =>
            prevSelectedGroups.some((prevGroup) => prevGroup.id === group.id)
          );

          const allObjectsFromGroups = newSelectedGroups.flatMap(
            (group) => group.objectList
          );

          // semua object yang ada di active object + object baru di grup
          const finalActiveObjects = allObjectsFromGroups.filter(
            (object) =>
              objectIdList.includes(object.id) ||
              !prevSelectedGroupIds.includes(object.id)
          );
          dispatch(reducerUpdateObjectListByObjectType(finalActiveObjects));
          dispatch(
            reducerUpdateAnalyticArgumentObjectIdList(
              finalActiveObjects.map((object) => object.id)
            )
          );
          // if no group selected
        } else {
          const newObjectList = responses[0].data.ecommerce_getObject;
          // get only new objects
          const newObjects = newObjectList.filter((object) => {
            const prevObjectListIds = objectList.map((object) => object.id);
            return !prevObjectListIds.includes(object.id);
          });

          const selectedObjects = newObjectList.filter((object) =>
            objectIdList.includes(object.id)
          );
          const finalActiveObjects = concatUniqueObjectsById(
            selectedObjects,
            newObjects
          );

          dispatch(reducerUpdateObjectListByObjectType(finalActiveObjects));
          dispatch(
            reducerUpdateAnalyticArgumentObjectIdList(
              finalActiveObjects.map((object) => object.id)
            )
          );
        }
      })
      .catch((error) =>
        handleClickAlert({
          vertical: 'top',
          horizontal: 'center',
          message: "Couldn't fetch data, please try again.",
          severity: 'error'
        })
      );
  };

  const isCurrentlyOnTourStep9 =
    isOnTour &&
    stepNumber === 9 &&
    menuStep === 'social-media' &&
    submenu === 'overview';

  const theme = useTheme();
  const matchesUpSm = useMediaQuery(theme.breakpoints.up('sm'));

  return (
    <>
      <HeaderWrapper
        display="flex"
        alignItems="center"
        isOnTour={isOnTour}
        ref={refs['socmed-overview']['group-object']}
      >
        <Grid container>
          <Grid item md={12}>
            <Stack direction="row" alignItems="center">
              <Typography
                variant="h1"
                sx={{
                  mb: pathname !== ONLINE_MEDIA_CLIPPING ? 0 : 2,
                  mr: 1
                }}
              >
                {setTitle()}
              </Typography>
              <TooltipCustomize title={textTooltip()} placement="top-start">
                <InfoOutlinedIcon
                  fontSize="small"
                  sx={{ transform: 'translateY(-4px)' }}
                />
              </TooltipCustomize>
            </Stack>
          </Grid>
          <Grid
            item
            md={12}
            width="inherit"
            sx={
              isCurrentlyOnTourStep9
                ? {
                    position: 'relative',
                    zIndex: 1000,
                    backgroundColor: 'white'
                  }
                : {}
            }
          >
            <GroupObject />
          </Grid>
          <ProductTourTooltip open={isCurrentlyOnTourStep9} placement="bottom">
            <Grid
              item
              md={12}
              sx={
                isCurrentlyOnTourStep9
                  ? {
                      position: 'relative',
                      zIndex: 1000,
                      backgroundColor: 'white'
                    }
                  : {}
              }
            >
              <Stack
                direction={matchesUpSm ? 'row' : 'column'}
                alignItems={matchesUpSm ? 'center' : 'start'}
                spacing={1}
                mt={1.5}
                mb={2.1}
              >
                {isEcommerce || isGoogleReviewMenu ? (
                  <ModalManageObjectByGroup title="Objects" />
                ) : (
                  <ModalManageObjectRetail title="Objects" />
                )}
                {isEcommerce ? (
                  <EcommerceObjectPreview objectList={objectListPrevies} />
                ) : (
                  <ObjectPreview objectList={objectListPrevies} />
                )}
              </Stack>
            </Grid>
          </ProductTourTooltip>
          {!isClustering() ? (
            <Grid item md={12}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                {isNetworkAnalysis() ? (
                  <TabNetworkAnalysis
                    tab={tab}
                    handleChange={handleChange}
                    label={title}
                  />
                ) : (
                  <Tabs
                    value={tab !== 'stream_preview' ? 'widget' : tab}
                    onChange={handleChange}
                    aria-label="basic tabs example"
                  >
                    <Tab label="Widget" value="widget" />
                    {isGoogleReviewMenu || isEcommerce ? null : (
                      <Tab label="Stream Preview" value="stream_preview" />
                    )}
                  </Tabs>
                )}
              </Box>
            </Grid>
          ) : null}
        </Grid>
      </HeaderWrapper>
      <HeaderWrapperSec display="flex" alignItems="center" isOnTour={isOnTour}>
        <Grid container>
          <Grid item sm={12}>
            {isEcommerce ? (
              <BottomHeaderEcommerce key={title} onRefresh={handleRefresh} />
            ) : (
              <BottomHeader key={title} />
            )}
          </Grid>
        </Grid>
      </HeaderWrapperSec>
    </>
  );
}

export default Header;
